import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CardsSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import { formatAmountMinor, resolveFinanceLocale } from './finance-format';
import { isRecord } from './finance-view-guards';
import { useFinanceView } from './useFinanceView';

type IncomeStatementRow = { code: string; label: string; subtotal: boolean };

type IncomeStatementColumn = {
  fiscalYear: number;
  basis: 'filed' | 'modelled';
  values: Record<string, number>;
  excluded: { movementCount: number; amountMinor: number } | null;
};

type IncomeStatementUnmodelledExclusion = {
  fiscalYear: number;
  movementCount: number;
  amountMinor: number;
};

type IncomeStatement = {
  organizationId: string;
  rows: IncomeStatementRow[];
  columns: IncomeStatementColumn[];
  unmodelledExclusions: IncomeStatementUnmodelledExclusion[];
  taxRateBasisPoints: number;
};

function isIncomeStatement(value: unknown): value is IncomeStatement {
  if (!isRecord(value)) {
    return false;
  }
  const rowsValid =
    Array.isArray(value.rows) &&
    value.rows.every(
      (row) =>
        isRecord(row) &&
        typeof row.code === 'string' &&
        typeof row.label === 'string' &&
        typeof row.subtotal === 'boolean',
    );
  const columnsValid =
    Array.isArray(value.columns) &&
    value.columns.every(
      (column) =>
        isRecord(column) &&
        typeof column.fiscalYear === 'number' &&
        (column.basis === 'filed' || column.basis === 'modelled') &&
        isRecord(column.values) &&
        (column.excluded === null ||
          (isRecord(column.excluded) &&
            typeof column.excluded.movementCount === 'number' &&
            typeof column.excluded.amountMinor === 'number')),
    );

  const unmodelledValid =
    Array.isArray(value.unmodelledExclusions) &&
    value.unmodelledExclusions.every(
      (exclusion) =>
        isRecord(exclusion) &&
        typeof exclusion.fiscalYear === 'number' &&
        typeof exclusion.movementCount === 'number' &&
        typeof exclusion.amountMinor === 'number',
    );

  return (
    typeof value.organizationId === 'string' &&
    typeof value.taxRateBasisPoints === 'number' &&
    rowsValid &&
    columnsValid &&
    unmodelledValid
  );
}

/** Basis points to a percentage the screen can print, without a float in the arithmetic. */
function formatBasisPoints(basisPoints: number, locale: string): string {
  return new Intl.NumberFormat(locale === 'it' ? 'it-IT' : 'en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(basisPoints / 10_000);
}

function StatementTable({
  rows,
  columns,
  locale,
}: {
  readonly rows: IncomeStatement['rows'];
  readonly columns: IncomeStatement['columns'];
  readonly locale: ReturnType<typeof resolveFinanceLocale>;
}) {
  const { t } = useTranslation('finance');
  return (
    <div className="mt-6 overflow-x-auto">
      <table className="w-full min-w-3xl text-sm">
        <caption className="sr-only">{t('incomeStatement.tableCaption')}</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className="py-2 text-left font-medium">
              {t('incomeStatement.lineColumn')}
            </th>
            {columns.map((column) => (
              <th
                key={`${column.basis}-${column.fiscalYear}`}
                scope="col"
                className="py-2 pl-4 text-right font-medium"
              >
                <span className="block">{column.fiscalYear}</span>
                <span className="text-foreground/60 block text-xs font-normal">
                  {t(`incomeStatement.basis.${column.basis}`)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.code}
              className={
                row.subtotal ? 'border-border border-t font-semibold' : 'text-foreground/90'
              }
            >
              <th scope="row" className="py-1.5 text-left font-inherit">
                {row.label}
              </th>
              {columns.map((column) => (
                <td
                  key={`${column.basis}-${column.fiscalYear}`}
                  className="py-1.5 pl-4 text-right tabular-nums"
                >
                  {formatAmountMinor(column.values[row.code] ?? 0, locale)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExcludedPanel({
  exclusions,
  locale,
}: {
  readonly exclusions: readonly IncomeStatementColumn[];
  readonly locale: ReturnType<typeof resolveFinanceLocale>;
}) {
  const { t } = useTranslation('finance');
  return (
    <div className="border-border mt-4 rounded-md border p-3">
      <h2 className="text-sm font-medium">{t('incomeStatement.excluded.title')}</h2>
      <p className="text-foreground/60 mt-1 text-xs">{t('incomeStatement.excluded.explanation')}</p>
      <ul className="mt-2 space-y-1 text-xs">
        {exclusions.map((column) => (
          <li key={column.fiscalYear}>
            {t('incomeStatement.excluded.line', {
              year: column.fiscalYear,
              count: column.excluded?.movementCount ?? 0,
              amount: formatAmountMinor(column.excluded?.amountMinor ?? 0, locale),
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FinanceIncomeStatementPage() {
  const { t, i18n } = useTranslation('finance');
  const locale = resolveFinanceLocale(i18n.resolvedLanguage);

  const request = useCallback(() => apiClient.GET('/v1/finance/income-statement', {}), []);
  const view = useFinanceView(request, isIncomeStatement);

  const exclusions = useMemo(
    () =>
      view.status === 'ready'
        ? view.data.columns.filter(
            (column) => column.excluded !== null && column.excluded.movementCount > 0,
          )
        : [],
    [view],
  );

  if (view.status === 'loading') {
    return <CardsSkeleton />;
  }

  if (view.status === 'error') {
    return (
      <div className="p-6">
        <p className="text-sm">{t('incomeStatement.loadError')}</p>
        <Button className="mt-3" onClick={view.reload}>
          {t('dashboard.retry')}
        </Button>
      </div>
    );
  }

  const { rows, columns, taxRateBasisPoints, unmodelledExclusions } = view.data;

  return (
    <section className="p-6">
      <h2 className="screen-title">{t('incomeStatement.title')}</h2>
      <p className="text-foreground/60 mt-1 text-sm">{t('incomeStatement.subtitle')}</p>

      {columns.length === 0 ? (
        <p className="mt-6 text-sm">{t('incomeStatement.empty')}</p>
      ) : (
        <StatementTable {...{ rows, columns, locale }} />
      )}

      <p className="text-foreground/60 mt-4 text-xs">
        {t('incomeStatement.taxRateNote', {
          rate: formatBasisPoints(taxRateBasisPoints, locale),
        })}
      </p>

      {unmodelledExclusions.length > 0 && (
        <div className="border-border mt-4 rounded-md border p-3">
          <h2 className="text-sm font-medium">{t('incomeStatement.unmodelled.title')}</h2>
          <p className="text-foreground/60 mt-1 text-xs">
            {t('incomeStatement.unmodelled.explanation')}
          </p>
          <ul className="mt-2 space-y-1 text-xs">
            {unmodelledExclusions.map((exclusion) => (
              <li key={exclusion.fiscalYear}>
                {t('incomeStatement.excluded.line', {
                  year: exclusion.fiscalYear,
                  count: exclusion.movementCount,
                  amount: formatAmountMinor(exclusion.amountMinor, locale),
                })}
              </li>
            ))}
          </ul>
        </div>
      )}

      {exclusions.length > 0 && <ExcludedPanel exclusions={exclusions} locale={locale} />}
    </section>
  );
}
